import { z } from 'zod'

import { PublicPackagingDetailsSchema } from './catalog'
import { LocaleSchema } from './common'
import { InquiryFieldsSchema, LocalBusinessSchema } from './content'
import { TemplateIdSchema } from './templates'
import { PublicContentTypeSchema } from './content-types'
import { PublicTrackingSchema } from './tracking'

// Locale-resolved read models served to the public site (apps/web). Strings are
// already resolved for the requested locale; rich text is sanitized HTML.

export const PublicImageSchema = z.object({
  url: z.string(),
  alt: z.string(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
})
export type PublicImage = z.infer<typeof PublicImageSchema>

export const PublicSeoSchema = z.object({
  title: z.string(),
  description: z.string(),
  ogImage: PublicImageSchema.nullable(),
  canonicalPath: z.string().nullable(),
  noindex: z.boolean(),
})
export type PublicSeo = z.infer<typeof PublicSeoSchema>

export const PublicLinkSchema = z.object({ label: z.string(), href: z.string() })
export const PublicFaqSchema = z.object({ question: z.string(), answer: z.string() })
export type PublicFaq = z.infer<typeof PublicFaqSchema>

const blockBase = { id: z.string(), anchor: z.string() }

export const PublicBlockSchema = z.discriminatedUnion('type', [
  z.object({
    ...blockBase,
    type: z.literal('hero'),
    variant: z.enum(['fullBleed', 'split', 'text', 'statement', 'video', 'centered']),
    eyebrow: z.string(),
    heading: z.string(),
    body: z.string(),
    image: PublicImageSchema.nullable(),
    /** YouTube video id (already extracted), or ''. */
    video: z.string(),
    links: z.array(PublicLinkSchema),
  }),
  z.object({
    ...blockBase,
    type: z.literal('split'),
    eyebrow: z.string(),
    heading: z.string(),
    bodyHtml: z.string(),
    image: PublicImageSchema.nullable(),
    imageSide: z.enum(['left', 'right']),
    background: z.enum(['ivory', 'linen', 'bone', 'ink']),
    imageStyle: z.enum(['bleed', 'card']),
    stats: z.array(z.object({ value: z.string(), label: z.string() })),
  }),
  z.object({ ...blockBase, type: z.literal('stats'), items: z.array(z.object({ value: z.string(), label: z.string() })) }),
  z.object({
    ...blockBase,
    type: z.literal('cardGrid'),
    eyebrow: z.string(),
    heading: z.string(),
    intro: z.string(),
    image: PublicImageSchema.nullable(),
    numbered: z.boolean(),
    link: PublicLinkSchema.nullable(),
    columns: z.number().int(),
    layout: z.enum(['grid', 'carousel', 'bordered', 'aside', 'numbered', 'certificates', 'facts', 'gallery', 'levels', 'definitions']),
    background: z.enum(['ivory', 'linen', 'bone']),
    cardLinkLabel: z.string(),
    pointsHeading: z.string(),
    excludedHeading: z.string(),
    cards: z.array(
      z.object({
        title: z.string(),
        body: z.string(),
        image: PublicImageSchema.nullable(),
        href: z.string(),
        label: z.string(),
        tagline: z.string(),
        points: z.array(z.string()),
        excluded: z.array(z.string()),
      }),
    ),
  }),
  z.object({ ...blockBase, type: z.literal('content'), bodyHtml: z.string(), heading: z.string(), style: z.enum(['prose', 'legal']) }),
  z.object({ ...blockBase, type: z.literal('faq'), eyebrow: z.string(), heading: z.string(), layout: z.enum(['list', 'columns']), items: z.array(PublicFaqSchema) }),
  z.object({
    ...blockBase,
    type: z.literal('quotes'),
    eyebrow: z.string(),
    heading: z.string(),
    items: z.array(z.object({ quote: z.string(), author: z.string(), organisation: z.string() })),
  }),
  z.object({
    ...blockBase,
    type: z.literal('imageGrid'),
    eyebrow: z.string(),
    heading: z.string(),
    columns: z.number().int(),
    variant: z.enum(['tiles', 'logos']),
    items: z.array(z.object({ image: PublicImageSchema.nullable(), caption: z.string(), href: z.string() })),
  }),
  z.object({
    ...blockBase,
    type: z.literal('catalogue'),
    source: z.enum(['industries', 'families', 'index']),
    eyebrow: z.string(),
    heading: z.string(),
    body: z.string(),
    link: PublicLinkSchema.nullable(),
    cardLinkLabel: z.string(),
    countLabel: z.string(),
    background: z.enum(['ivory', 'linen', 'bone']),
    extraHeading: z.string(),
    extraLinks: z.array(PublicLinkSchema),
  }),
  z.object({
    ...blockBase,
    type: z.literal('portfolio'),
    heading: z.string(),
    intro: z.string(),
    allLabel: z.string(),
    countLabel: z.string(),
    filters: z.array(z.object({ key: z.string(), label: z.string() })),
    items: z.array(
      z.object({ image: PublicImageSchema.nullable(), title: z.string(), client: z.string(), tag: z.string(), filter: z.string(), wide: z.boolean() }),
    ),
  }),
  z.object({
    ...blockBase,
    type: z.literal('spotlight'),
    eyebrow: z.string(),
    heading: z.string(),
    image: PublicImageSchema.nullable(),
    sideEyebrow: z.string(),
    sideTitle: z.string(),
    body: z.string(),
    points: z.array(z.string()),
    background: z.enum(['ivory', 'linen', 'bone']),
  }),
  z.object({
    ...blockBase,
    type: z.literal('columns'),
    variant: z.enum(['ruled', 'callout', 'centered']),
    eyebrow: z.string(),
    heading: z.string(),
    body: z.string(),
    rows: z.array(z.object({ label: z.string(), value: z.string(), href: z.string() })),
    link: PublicLinkSchema.nullable(),
    background: z.enum(['ivory', 'linen', 'bone']),
  }),
  z.object({
    ...blockBase,
    type: z.literal('team'),
    eyebrow: z.string(),
    heading: z.string(),
    intro: z.string(),
    groups: z.array(
      z.object({
        label: z.string(),
        people: z.array(z.object({ image: PublicImageSchema.nullable(), badge: z.string(), role: z.string(), name: z.string(), bio: z.string() })),
        roles: z.array(z.object({ title: z.string(), body: z.string() })),
      }),
    ),
    link: PublicLinkSchema.nullable(),
  }),
  z.object({
    ...blockBase,
    type: z.literal('showcase'),
    label: z.string(),
    heading: z.string(),
    quote: z.string(),
    tags: z.array(z.string()),
    credit: z.string(),
    body: z.string(),
    images: z.array(PublicImageSchema),
    background: z.enum(['ivory', 'bone']),
  }),
  z.object({ ...blockBase, type: z.literal('strip'), image: PublicImageSchema.nullable() }),
  z.object({
    ...blockBase,
    type: z.literal('swatches'),
    kind: z.enum(['material', 'finish']),
    groups: z.array(z.object({ key: z.string(), title: z.string(), note: z.string() })),
    showNav: z.boolean(),
    background: z.enum(['ivory', 'linen', 'bone']),
  }),
  z.object({
    ...blockBase,
    type: z.literal('guide'),
    heading: z.string(),
    body: z.string(),
    items: z.array(z.object({ title: z.string(), body: z.string() })),
    background: z.enum(['ivory', 'linen', 'bone']),
  }),
  z.object({
    ...blockBase,
    type: z.literal('cta'),
    variant: z.enum(['centered', 'band', 'left', 'form']),
    eyebrow: z.string(),
    heading: z.string(),
    body: z.string(),
    links: z.array(PublicLinkSchema),
    /** Absent from an API older than the `form` variant. */
    image: PublicImageSchema.nullable().default(null),
  }),
  z.object({
    ...blockBase,
    type: z.literal('booking'),
    eyebrow: z.string(),
    heading: z.string(),
    body: z.string(),
    url: z.string(),
    height: z.number().int(),
    linkLabel: z.string(),
    background: z.enum(['ivory', 'linen', 'bone']),
  }),
  z.object({
    ...blockBase,
    type: z.literal('game'),
    heading: z.string(),
    intro: z.string(),
    tag: z.string(),
    title: z.string(),
    body: z.string(),
    image: PublicImageSchema.nullable(),
    mascot: PublicImageSchema.nullable(),
    /** `url` is set only for a `live` store whose link passed the host check; any other live store reads as `soon`. */
    stores: z.object({
      googlePlay: z.object({ status: z.enum(['off', 'soon', 'live']), url: z.string() }),
      appStore: z.object({ status: z.enum(['off', 'soon', 'live']), url: z.string() }),
    }),
    link: PublicLinkSchema.nullable(),
    moreLink: PublicLinkSchema.nullable(),
    screenshots: z.array(z.object({ image: PublicImageSchema, caption: z.string() })),
  }),
])
export type PublicBlock = z.infer<typeof PublicBlockSchema>

export const PublicPageSchema = z.object({
  slug: z.string(),
  title: z.string(),
  blocks: z.array(PublicBlockSchema),
  seo: PublicSeoSchema,
  updatedAt: z.string(),
})
export type PublicPage = z.infer<typeof PublicPageSchema>

export const PublicSwatchSchema = z.object({
  slug: z.string(),
  kind: z.enum(['material', 'finish']),
  category: z.string(),
  title: z.string(),
  description: z.string(),
  note: z.string(),
  image: PublicImageSchema.nullable(),
  guide: PublicLinkSchema.nullable(),
})
export type PublicSwatch = z.infer<typeof PublicSwatchSchema>

/** What a landing page shows from the swatch library, resolved for its spec groups. */
export const PublicSpecSchema = z.object({
  materials: z.array(PublicSwatchSchema),
  /** Swatches that apply to the page in total (the strip shows the first few). */
  materialsTotal: z.number().int(),
  finishes: z.array(PublicSwatchSchema),
  finishesTotal: z.number().int(),
  terms: z.array(z.object({ label: z.string(), value: z.string() })),
  costDrivers: z.array(z.object({ name: z.string(), description: z.string() })),
})
export type PublicSpec = z.infer<typeof PublicSpecSchema>

export const PublicCategoryCardSchema = z.object({
  slug: z.string(),
  title: z.string(),
  tagline: z.string(),
  summary: z.string(),
  heroImage: PublicImageSchema.nullable(),
  /** Published products, in order (title + slug only). */
  products: z.array(z.object({ slug: z.string(), title: z.string() })),
})
export type PublicCategoryCard = z.infer<typeof PublicCategoryCardSchema>

export const PublicProductCardSchema = z.object({
  slug: z.string(),
  categorySlug: z.string(),
  title: z.string(),
  summary: z.string(),
  image: PublicImageSchema.nullable(),
})
export type PublicProductCard = z.infer<typeof PublicProductCardSchema>

export const PublicCategorySchema = PublicCategoryCardSchema.extend({
  summary: z.string(),
  gallery: z.array(PublicImageSchema),
  bodyHtml: z.string(),
  details: PublicPackagingDetailsSchema,
  spec: PublicSpecSchema,
  faqs: z.array(PublicFaqSchema),
  products: z.array(PublicProductCardSchema),
  relatedArticles: z.array(z.lazy(() => PublicArticleCardSchema)),
  relatedLinks: z.array(PublicLinkSchema),
  seo: PublicSeoSchema,
  updatedAt: z.string(),
})
export type PublicCategory = z.infer<typeof PublicCategorySchema>

export const PublicProductSchema = PublicProductCardSchema.extend({
  categoryTitle: z.string(),
  gallery: z.array(PublicImageSchema),
  bodyHtml: z.string(),
  details: PublicPackagingDetailsSchema,
  spec: PublicSpecSchema,
  faqs: z.array(PublicFaqSchema),
  relatedArticles: z.array(z.lazy(() => PublicArticleCardSchema)),
  relatedLinks: z.array(PublicLinkSchema),
  seo: PublicSeoSchema,
  updatedAt: z.string(),
})
export type PublicProduct = z.infer<typeof PublicProductSchema>

export const PublicIndustryCardSchema = z.object({
  slug: z.string(),
  title: z.string(),
  risk: z.string(),
  heroImage: PublicImageSchema.nullable(),
})
export type PublicIndustryCard = z.infer<typeof PublicIndustryCardSchema>

export const PublicIndustrySchema = PublicIndustryCardSchema.extend({
  summary: z.string(),
  gallery: z.array(PublicImageSchema),
  bodyHtml: z.string(),
  details: PublicPackagingDetailsSchema,
  spec: PublicSpecSchema,
  faqs: z.array(PublicFaqSchema),
  recommendedCategories: z.array(PublicCategoryCardSchema),
  recommendedProducts: z.array(PublicProductCardSchema),
  relatedArticles: z.array(z.lazy(() => PublicArticleCardSchema)),
  seo: PublicSeoSchema,
  updatedAt: z.string(),
})
export type PublicIndustry = z.infer<typeof PublicIndustrySchema>

export const PublicArticleCardSchema = z.object({
  slug: z.string(),
  section: z.enum(['journal', 'news']),
  title: z.string(),
  category: z.string(),
  excerpt: z.string(),
  heroImage: PublicImageSchema.nullable(),
  publishedAt: z.string().nullable(),
  featured: z.boolean(),
})
export type PublicArticleCard = z.infer<typeof PublicArticleCardSchema>

export const PublicArticleSchema = PublicArticleCardSchema.extend({
  bodyHtml: z.string(),
  faqs: z.array(PublicFaqSchema),
  author: z.string(),
  seo: PublicSeoSchema,
  updatedAt: z.string(),
  /** Enabled locales the article is written in (its own title and body), not a fallback to another language. */
  locales: z.array(LocaleSchema).default([]),
})
export type PublicArticle = z.infer<typeof PublicArticleSchema>

export const PublicSiteSchema = z.object({
  /** Locale this response was resolved for. */
  locale: LocaleSchema,
  primaryLocale: LocaleSchema,
  enabledLocales: z.array(LocaleSchema),
  siteName: z.string(),
  titleTemplate: z.string(),
  defaultTitle: z.string(),
  defaultDescription: z.string(),
  defaultOgImage: PublicImageSchema.nullable(),
  tagline: z.string(),
  legalName: z.string(),
  foundingYear: z.number().int().nullable(),
  address: z.object({
    street: z.string(),
    city: z.string(),
    region: z.string(),
    postalCode: z.string(),
    country: z.string(),
  }),
  phone: z.string(),
  email: z.string(),
  whatsapp: z.string(),
  /** The site's design. Absent from an API older than templates: the original one. */
  templateId: TemplateIdSchema.optional(),
  /** Logo, favicon and accent colour overrides, resolved. */
  branding: z.object({ logo: PublicImageSchema.nullable(), favicon: PublicImageSchema.nullable(), themeColor: z.string() }).optional(),
  /** The site's own wording for the swatch library, resolved. Absent from an API older than this: use the built-in wording. */
  swatchNames: z.object({ plural: z.string(), material: z.string(), finish: z.string() }).optional(),
  /** Opening hours, geo position and business type for local-search markup. Absent from an API older than local SEO. */
  localBusiness: LocalBusinessSchema.optional(),
  /** Which optional quote-form fields are shown or required. Absent from an API older than form settings: the original form. */
  inquiryFields: InquiryFieldsSchema.optional(),
  socials: z.array(z.object({ platform: z.string(), url: z.string() })),
  tracking: PublicTrackingSchema,
  /** The site's content types that are switched on, named for `locale`. Absent from an API older than content types: read that as "all built-in sections on". */
  types: z.array(PublicContentTypeSchema).optional(),
  /** Public Turnstile site key; empty means the contact form renders no captcha. */
  turnstileSiteKey: z.string(),
  /** A demo website of a made-up business (API var SITE_DEMO, or SHOP_DEMO): every page shows the demo notice and asks search engines not to list it. Absent from an older API: not a demo. */
  demo: z.boolean().default(false),
  /** A site not yet public (API var SITE_PREVIEW): every page shows a small "Preview" ribbon. Separate from `demo`. Absent from an older API: off. */
  preview: z.boolean().default(false),
  /** The site's package has the quote form (flag `lead_capture`): without it there is no contact page and the API refuses inquiries. Absent from an older API: it has. */
  leadCapture: z.boolean().default(true),
  googleSiteVerification: z.string(),
  twitterHandle: z.string(),
  header: z.object({
    items: z.array(
      PublicLinkSchema.extend({
        children: z.array(PublicLinkSchema.extend({ description: z.string() })),
        mega: z.boolean(),
        megaHeading: z.string(),
      }),
    ),
    cta: PublicLinkSchema.nullable(),
  }),
  footer: z.object({
    eyebrow: z.string(),
    heading: z.string(),
    cta: PublicLinkSchema.nullable(),
    columns: z.array(z.object({ title: z.string(), links: z.array(PublicLinkSchema) })),
    legalLinks: z.array(PublicLinkSchema),
  }),
})
export type PublicSite = z.infer<typeof PublicSiteSchema>

export const SitemapEntrySchema = z.object({ path: z.string(), updatedAt: z.string(), noindex: z.boolean() })
export const SitemapResponseSchema = z.object({ entries: z.array(SitemapEntrySchema) })
